/**
 * PartnerPage — il programma partner di YumaOS (19/10/2026): chi ci porta aziende, quanto matura sugli abbonamenti
 * dei posti, quando lo paghiamo. Cinque schede: partner, aziende, liquidazioni, profili e regimi, e il kit (i materiali che
 * i partner mandano ai loro contatti, coi messaggi pronti: 03/11/2026).
 *
 * Legge direttamente le raccolte `platform_partner*` (regola generale del super utente); ogni scrittura passa dalle
 * funzioni `partnerConsole*`. Doc: docs/01-ARCHITECTURE/PROGRAMMA-PARTNER-MODEL.md.
 */

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Alert, Box, CircularProgress, IconButton, Snackbar, Stack, Tab, Tabs, Tooltip, Typography } from '@mui/material';
import { Refresh } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { tokens } from '../theme';
import {
  leggiProgrammaPartner, messaggioDiErrore, type DatiProgrammaPartner,
} from '../services/partnerConsoleService';
import { SchedaPartner } from '../components/partner/SchedaPartner';
import { SchedaAziende } from '../components/partner/SchedaAziende';
import { SchedaLiquidazioni } from '../components/partner/SchedaLiquidazioni';
import { SchedaProfili } from '../components/partner/SchedaProfili';
import { SchedaKit } from '../components/partner/SchedaKit';
import type { Esegui } from '../components/partner/esegui';
import { useFormati } from '../components/partner/formati';

type Scheda = 'partner' | 'aziende' | 'liquidazioni' | 'profili' | 'kit';

const Metrica: React.FC<{ etichetta: string; valore: string; colore?: string }> = ({ etichetta, valore, colore }) => (
  <Box sx={{ minWidth: 0 }}>
    <Typography sx={{ fontSize: '0.68rem', fontWeight: 600, color: tokens.color.ash }}>{etichetta}</Typography>
    <Typography sx={{ fontFamily: tokens.font.display, fontWeight: 600, fontSize: '1.35rem', color: colore ?? tokens.color.graphite, lineHeight: 1.2 }}>
      {valore}
    </Typography>
  </Box>
);

const PartnerPage: React.FC = () => {
  const { t } = useTranslation('partner');
  const f = useFormati();
  const [dati, setDati] = useState<DatiProgrammaPartner | null>(null);
  const [scheda, setScheda] = useState<Scheda>('partner');
  const [caricamento, setCaricamento] = useState(true);
  const [errore, setErrore] = useState<string | null>(null);
  const [messaggio, setMessaggio] = useState<{ testo: string; gravita: 'success' | 'error' } | null>(null);

  const carica = useCallback(async () => {
    setCaricamento(true);
    setErrore(null);
    try {
      setDati(await leggiProgrammaPartner());
    } catch (e) {
      setErrore(messaggioDiErrore(e));
    } finally {
      setCaricamento(false);
    }
  }, []);

  useEffect(() => { void carica(); }, [carica]);

  const dillo = (testo: string, gravita: 'success' | 'error') => setMessaggio({ testo, gravita });
  const erroreDi = (m: string) => dillo(t('messaggi.errore', { messaggio: m }), 'error');

  const esegui: Esegui = async (azione, riuscito) => {
    try {
      const risultato = await azione();
      dillo(typeof riuscito === 'function' ? riuscito(risultato) : riuscito ?? t('messaggi.salvato'), 'success');
      await carica();
      return true;
    } catch (e) {
      erroreDi(messaggioDiErrore(e));
      return false;
    }
  };

  const metriche = useMemo(() => {
    if (!dati) return null;
    const daPagare = new Map<string, number>();
    for (const l of dati.liquidazioni) {
      if (l.stato === 'approvata' || l.stato === 'fattura_ricevuta') daPagare.set(l.valuta, (daPagare.get(l.valuta) ?? 0) + l.netto_centesimi);
    }
    return {
      attivi: dati.partner.filter((p) => p.stato === 'attivo' && p.dimostrativo !== true).length,
      aziende: dati.abbinamenti.filter((a) => a.stato === 'attiva').length,
      daDecidere: dati.abbinamenti.filter((a) => a.stato === 'da_decidere').length,
      daPagare: daPagare.size ? [...daPagare].map(([valuta, c]) => f.soldi(c, valuta)).join(', ') : f.soldi(0),
    };
  }, [dati, f]);

  const ricarica = () => void carica();

  return (
    <Box>
      <Stack direction={{ xs: 'column', lg: 'row' }} justifyContent="space-between" alignItems={{ xs: 'flex-start', lg: 'center' }} spacing={2} mb={3}>
        <Box sx={{ minWidth: 0 }}>
          <Typography sx={{
            fontFamily: tokens.font.display, fontWeight: 600, fontSize: { xs: '1.6rem', md: '1.95rem' }, lineHeight: 1.1,
            letterSpacing: '-0.02em', color: tokens.color.graphite, fontVariationSettings: '"WONK" 0, "SOFT" 30',
          }}>
            {t('titolo')}
          </Typography>
          <Typography variant="body2" sx={{ color: tokens.color.ash, mt: 0.5, maxWidth: 560 }}>{t('sottotitolo')}</Typography>
        </Box>
        <Stack direction="row" spacing={3} alignItems="center" sx={{ flexShrink: 0, flexWrap: 'wrap', rowGap: 1.5 }}>
          {metriche && (
            <>
              <Metrica etichetta={t('metriche.partner_attivi')} valore={String(metriche.attivi)} />
              <Metrica etichetta={t('metriche.aziende_attive')} valore={String(metriche.aziende)} colore={tokens.color.success} />
              <Metrica etichetta={t('metriche.da_decidere')} valore={String(metriche.daDecidere)} colore={metriche.daDecidere ? tokens.color.warning : undefined} />
              <Metrica etichetta={t('metriche.da_pagare')} valore={metriche.daPagare} />
            </>
          )}
          <Tooltip title={t('azioni.ricarica')}>
            <span>
              <IconButton onClick={ricarica} disabled={caricamento} size="small"><Refresh fontSize="small" /></IconButton>
            </span>
          </Tooltip>
        </Stack>
      </Stack>

      {errore && <Alert severity="error" sx={{ mb: 2 }}>{errore}</Alert>}

      <Tabs value={scheda} onChange={(_e, v: Scheda) => setScheda(v)} sx={{ mb: 2, borderBottom: `1px solid ${tokens.color.line}` }} variant="scrollable" allowScrollButtonsMobile>
        <Tab value="partner" label={t('schede.partner')} sx={{ textTransform: 'none' }} />
        <Tab value="aziende" label={t('schede.aziende')} sx={{ textTransform: 'none' }} />
        <Tab value="liquidazioni" label={t('schede.liquidazioni')} sx={{ textTransform: 'none' }} />
        <Tab value="profili" label={t('schede.profili')} sx={{ textTransform: 'none' }} />
        <Tab value="kit" label={t('schede.kit')} sx={{ textTransform: 'none' }} />
      </Tabs>

      {!dati ? (
        <Box sx={{ p: 6, textAlign: 'center' }}>{caricamento && <CircularProgress size={28} />}</Box>
      ) : (
        <>
          {scheda === 'partner' && <SchedaPartner dati={dati} esegui={esegui} onErrore={erroreDi} onRicarica={ricarica} />}
          {scheda === 'aziende' && <SchedaAziende dati={dati} esegui={esegui} onErrore={erroreDi} onRicarica={ricarica} />}
          {scheda === 'liquidazioni' && <SchedaLiquidazioni dati={dati} esegui={esegui} />}
          {scheda === 'profili' && <SchedaProfili dati={dati} esegui={esegui} onErrore={erroreDi} onRicarica={ricarica} />}
          {scheda === 'kit' && <SchedaKit dati={dati} esegui={esegui} onErrore={erroreDi} onRicarica={ricarica} />}
        </>
      )}

      <Snackbar open={!!messaggio} autoHideDuration={6000} onClose={() => setMessaggio(null)} anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
        <Alert severity={messaggio?.gravita ?? 'success'} onClose={() => setMessaggio(null)} variant="filled">{messaggio?.testo}</Alert>
      </Snackbar>
    </Box>
  );
};

export default PartnerPage;
